import axios from 'axios';

// In production, use same-origin /api (Nginx proxies to backend). In dev, use local backend.
// API URL can be configured via REACT_APP_API_URL environment variable
// Default: https://api-test.swifttoolskit.cloud/api
// For local development with network access: http://192.168.1.192:5011/api
// For localhost only: http://localhost:5011/api
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api-test.swifttoolskit.cloud/api';

// Resolves a backend-relative path (e.g. "/uploads/foo.jpg") to a full URL.
// Strips the trailing "/api" from the base since uploaded files are served from the server root.
export const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  if (url.startsWith('/uploads/')) {
    return API_BASE_URL.replace(/\/api\/?$/, '') + url;
  }
  return url;
};

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const currentOrigin = window.location.origin;
    const requestUrl = error.config?.url ? `${API_BASE_URL}${error.config.url}` : API_BASE_URL;
    
    // Detect CORS errors
    const isCorsError = 
      error.code === 'ERR_NETWORK' ||
      !error.response ||
      error.message?.toLowerCase().includes('cors') ||
      error.message?.toLowerCase().includes('access-control') ||
      error.message?.toLowerCase().includes('failed to fetch') ||
      (error.response?.status === 0) ||
      (error.response?.status === undefined && error.request);

    if (isCorsError) {
      // Enhance error object with CORS-specific information
      error.isCorsError = true;
      error.corsDetails = {
        origin: currentOrigin,
        apiUrl: requestUrl,
        errorMessage: error.message || 'Network request failed',
        troubleshooting: [
          `Origin: ${currentOrigin}`,
          `API URL: ${requestUrl}`,
          'Possible CORS issue detected. Check:',
          '1. Backend server is running and accessible',
          '2. CORS_ORIGIN in backend/.env includes your origin',
          '3. Backend CORS configuration allows this origin',
          '4. Network connectivity between frontend and backend'
        ]
      };
      
      console.error('🚫 CORS Error Detected:');
      console.error(`Origin: ${currentOrigin}`);
      console.error(`API URL: ${requestUrl}`);
      console.error('Error:', error.message || 'Network request failed');
      console.error('Troubleshooting:');
      error.corsDetails.troubleshooting.forEach(step => console.error(`  ${step}`));
    } else if (error.code === 'ERR_NETWORK' || !error.response) {
      // General network error (not necessarily CORS)
      console.error('Network Error: Backend server is not reachable. Please ensure:');
      console.error(`1. Backend server is running on ${API_BASE_URL}`);
      console.error('2. Check if the backend server is started: cd backend && npm start');
      console.error('3. Verify the API URL is correct in your .env file');
    }
    
    if (error.response?.status === 401) {
      localStorage.removeItem('adminToken');
      window.location.href = '/admin/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  verify: () => api.get('/auth/verify'),
  logout: () => api.post('/auth/logout'),
};

// Home Content API
export const homeAPI = {
  get: () => api.get('/home'),
  updateHero: (data) => api.put('/home/hero', data),
  updateAbout: (data) => api.put('/home/about', data),
  updateNDIS: (data) => api.put('/home/ndis', data),
  updateConditions: (data) => api.put('/home/conditions', data),
  updateInsurance: (data) => api.put('/home/insurance', data),
};

// Services API
export const servicesAPI = {
  getAll: () => api.get('/services'),
  getAllAdmin: () => api.get('/services/all'),
  getById: (id) => api.get(`/services/${id}`),
  create: (data) => api.post('/services', data),
  update: (id, data) => api.put(`/services/${id}`, data),
  delete: (id) => api.delete(`/services/${id}`),
};

// Team API
export const teamAPI = {
  getAll: () => api.get('/team'),
  getAllAdmin: () => api.get('/team/all'),
  getById: (id) => api.get(`/team/${id}`),
  create: (data) => api.post('/team', data),
  update: (id, data) => api.put(`/team/${id}`, data),
  delete: (id) => api.delete(`/team/${id}`),
};

// Blog API
export const blogAPI = {
  getAll: (limit) => api.get('/blog', { params: { limit } }),
  getAllAdmin: () => api.get('/blog/all'),
  getById: (id) => api.get(`/blog/${id}`),
  create: (data) => api.post('/blog', data),
  update: (id, data) => api.put(`/blog/${id}`, data),
  delete: (id) => api.delete(`/blog/${id}`),
};

// Core Values API
export const coreValuesAPI = {
  getAll: () => api.get('/core-values'),
  getAllAdmin: () => api.get('/core-values/all'),
  create: (data) => api.post('/core-values', data),
  update: (id, data) => api.put(`/core-values/${id}`, data),
  delete: (id) => api.delete(`/core-values/${id}`),
};

// Insurance API
export const insuranceAPI = {
  getAll: () => api.get('/insurance'),
  getAllAdmin: () => api.get('/insurance/all'),
  create: (data) => api.post('/insurance', data),
  update: (id, data) => api.put(`/insurance/${id}`, data),
  delete: (id) => api.delete(`/insurance/${id}`),
};

// Conditions API
export const conditionsAPI = {
  getAll: () => api.get('/conditions'),
  getAllAdmin: () => api.get('/conditions/all'),
  getById: (id) => api.get(`/conditions/${id}`),
  create: (data) => api.post('/conditions', data),
  update: (id, data) => api.put(`/conditions/${id}`, data),
  delete: (id) => api.delete(`/conditions/${id}`),
};

// Settings API
export const settingsAPI = {
  get: () => api.get('/settings'),
  update: (data) => api.put('/settings', data),
  updateLogo: (type, url) => api.put('/settings/logo', { type, url }),
};

// Upload API
export const uploadAPI = {
  uploadImage: (formData) => {
    const token = localStorage.getItem('adminToken');
    return axios.post(`${API_BASE_URL}/upload/image`, formData, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  deleteImage: (filename) => api.delete(`/upload/image/${filename}`),
};

// Booking API
export const bookingAPI = {
  getConfig: () => api.get('/bookings/config'),
  getAvailableSlots: (date, location, practitioner) => api.get('/bookings/available', { params: { date, location, practitioner } }),
  create: (data) => api.post('/bookings', data),
  getAll: (params) => api.get('/bookings', { params }),
  getById: (id) => api.get(`/bookings/${id}`),
  update: (id, data) => api.put(`/bookings/${id}`, data),
  delete: (id) => api.delete(`/bookings/${id}`),
};

// Locations API
export const locationsAPI = {
  getAll: () => api.get('/locations'),
  getAllAdmin: () => api.get('/locations/all'),
  getById: (id) => api.get(`/locations/${id}`),
  create: (data) => api.post('/locations', data),
  update: (id, data) => api.put(`/locations/${id}`, data),
  delete: (id) => api.delete(`/locations/${id}`),
};

// Practitioners API
export const practitionersAPI = {
  getAll: () => api.get('/team/practitioners'),
};

// Fees API
export const feesAPI = {
  getAll: () => api.get('/fees'),
  getAllAdmin: () => api.get('/fees/all'),
  create: (data) => api.post('/fees', data),
  update: (id, data) => api.put(`/fees/${id}`, data),
  delete: (id) => api.delete(`/fees/${id}`),
};

// Job Listings API
export const jobListingsAPI = {
  getAll: () => api.get('/job-listings'),
  getAllAdmin: () => api.get('/job-listings/all'),
  create: (data) => api.post('/job-listings', data),
  update: (id, data) => api.put(`/job-listings/${id}`, data),
  delete: (id) => api.delete(`/job-listings/${id}`),
};

// Contact API
export const contactAPI = {
  submit: (data) => api.post('/contact', data),
  getAll: () => api.get('/contact'),
  updateStatus: (id, status) => api.put(`/contact/${id}`, { status }),
  delete: (id) => api.delete(`/contact/${id}`),
};

// FAQ API
export const faqAPI = {
  getAll: () => api.get('/faq'),
  getAllAdmin: () => api.get('/faq/all'),
  create: (data) => api.post('/faq', data),
  update: (id, data) => api.put(`/faq/${id}`, data),
  delete: (id) => api.delete(`/faq/${id}`),
};

// Work With Us API
export const workWithUsAPI = {
  getContent: () => api.get('/work-with-us'),
  updateContent: (data) => api.put('/work-with-us', data),
};

// Seed API
export const seedAPI = {
  importDemoData: () => api.post('/seed/demo-data'),
  deleteDemoData: () => api.delete('/seed/demo-data'),
};

export default api;
